import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Container, PageHeader, Spinner } from "@/components/shared";
import PremiumHint from "@/components/PremiumHint";
import { toast } from "sonner";
import { Copy, Bookmark, BookmarkCheck, Trash2, MessageSquare, Search, Sparkles, Star, X, RotateCw, Wand2, Pencil } from "lucide-react";

const TABS = [
  { id: "all", label: "Tout" },
  { id: "reply", label: "Réponses", icon: MessageSquare },
  { id: "analyze", label: "Analyses", icon: Search },
  { id: "content", label: "Scripts", icon: Sparkles },
  { id: "fav", label: "Mes meilleures réponses", icon: Star },
];

const KIND_LABEL = { reply: "Réponse", analyze: "Analyse", content: "Script" };
const KIND_ICON = { reply: MessageSquare, analyze: Search, content: Sparkles };

function summarize(it) {
  const d = it.data || {};
  if (it.kind === "reply") {
    const first = (d.replies || [])[0];
    return { head: d.comment || "Commentaire", body: first ? `${first.style} — ${first.text}` : (d.short || ""), copy: first?.text || d.short || "" };
  }
  if (it.kind === "analyze") {
    return { head: d.situation || "Situation", body: (d.interpretations || [])[0] || "", copy: (d.interpretations || []).join("\n") };
  }
  if (it.kind === "content") {
    return { head: `${d.platform || ""} · ${d.topic || ""}`, body: d.hook || "", copy: d.script || d.hook || "" };
  }
  return { head: it.title, body: "", copy: "" };
}

export default function History() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = tab === "fav" ? { favorite: true } : { kind: tab };
      const { data } = await api.get("/history", { params });
      setItems(data.items || []);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const toggleFav = async (it) => {
    try {
      const { data } = await api.post(`/history/${it.history_id}/favorite`);
      if (tab === "fav" && !data.favorite) setItems((x) => x.filter((i) => i.history_id !== it.history_id));
      else setItems((x) => x.map((i) => i.history_id === it.history_id ? { ...i, favorite: data.favorite } : i));
      toast.success(data.favorite ? "Ajouté aux favoris" : "Retiré des favoris");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const del = async (it) => {
    try {
      await api.delete(`/history/${it.history_id}`);
      setItems((x) => x.filter((i) => i.history_id !== it.history_id));
      if (sel?.history_id === it.history_id) setSel(null);
      toast.success("Supprimé");
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Container>
      <PageHeader eyebrow="Vos créations" title="Historique"
        subtitle="Ouvrez une ancienne recherche pour la reprendre : changez le ton, générez une nouvelle variante ou modifiez-la." />

      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`history-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-sm border transition-colors ${
              tab === t.id ? "border-gold bg-gold/15 text-gold" : "border-white/10 text-white/60 hover:border-white/20"}`}>
            {t.icon && <t.icon size={14} />} {t.label}
          </button>
        ))}
      </div>

      {loading && <Spinner label="Chargement..." />}

      {!loading && items.length === 0 && (
        <div className="card-surface p-10 text-center text-white/40">
          <p>Rien ici pour l'instant. Générez une réponse pour la retrouver ici.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((it, i) => {
          const s = summarize(it);
          const Icon = KIND_ICON[it.kind] || MessageSquare;
          return (
            <div key={it.history_id} data-testid={`history-item-${i}`} onClick={() => setSel(it)}
              className="card-surface p-5 fade-up cursor-pointer hover:border-gold/30 transition-colors">
              <div className="flex items-center justify-between mb-2">
                <span className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.15em]">
                  <Icon size={13} /> {KIND_LABEL[it.kind] || "Réponse"}
                </span>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <button data-testid={`history-copy-${i}`} onClick={() => { navigator.clipboard.writeText(s.copy); toast.success("Copié !"); }}
                    className="text-white/40 hover:text-gold"><Copy size={15} /></button>
                  <button data-testid={`history-fav-${i}`} onClick={() => toggleFav(it)} className="text-white/40 hover:text-gold">
                    {it.favorite ? <BookmarkCheck size={15} className="text-gold" /> : <Bookmark size={15} />}
                  </button>
                  <button data-testid={`history-del-${i}`} onClick={() => del(it)} className="text-white/40 hover:text-red-400"><Trash2 size={15} /></button>
                </div>
              </div>
              <p className="text-white/85 text-sm font-medium mb-1 line-clamp-2">{s.head}</p>
              {s.body && <p className="text-white/50 text-sm line-clamp-3">{s.body}</p>}
              <p className="text-white/30 text-xs mt-3">{new Date(it.created_at).toLocaleString("fr-FR")}</p>
            </div>
          );
        })}
      </div>

      <PremiumHint text="Premium sauvegarde votre historique complet et vos favoris sans limite." />

      {sel && <HistoryDetail it={sel} onClose={() => setSel(null)} navigate={navigate} onDelete={del} />}
    </Container>
  );
}

function ActionBtn({ onClick, icon: Icon, label, testid, primary }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
        primary ? "gold-gradient text-black" : "border border-white/15 text-white/75 hover:text-gold hover:border-gold/40"}`}>
      <Icon size={15} /> {label}
    </button>
  );
}

function HistoryDetail({ it, onClose, navigate, onDelete }) {
  const d = it.data || {};
  const replies = d.replies || [];
  const [toneIdx, setToneIdx] = useState(0);
  const current = replies[toneIdx];

  const enc = encodeURIComponent;
  const copy = (t) => { navigator.clipboard.writeText(t || ""); toast.success("Copié !"); };

  const goReprendre = () => {
    if (it.kind === "reply") navigate(`/reply?comment=${enc(d.comment || "")}`);
    else if (it.kind === "analyze") navigate(`/analyze?situation=${enc(d.situation || "")}`);
    else navigate(`/create?platform=${enc(d.platform || "Instagram")}&topic=${enc(d.topic || "")}`);
    onClose();
  };
  const goVariante = () => {
    if (it.kind === "reply") navigate(`/reply?comment=${enc(d.comment || "")}&auto=1`);
    else if (it.kind === "analyze") navigate(`/analyze?situation=${enc(d.situation || "")}&auto=1`);
    else navigate(`/create?platform=${enc(d.platform || "Instagram")}&topic=${enc(d.topic || "")}&auto=1`);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[800] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="history-detail">
      <div className="bg-[#0b0b0b] border border-white/10 w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0b0b0b] border-b border-white/10 px-5 py-3 flex items-center justify-between">
          <span className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.15em]">
            {KIND_LABEL[it.kind] || "Réponse"}
          </span>
          <button onClick={onClose} data-testid="history-detail-close" className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <p className="text-white/40 text-xs mb-1">Ta demande d'origine</p>
            <div className="card-surface p-3 text-sm text-white/85" data-testid="history-detail-original">
              {d.comment || d.situation || (d.platform ? `${d.platform} · ${d.topic || ""}` : "") || it.title}
            </div>
          </div>

          {it.kind === "reply" && replies.length > 0 && (
            <div>
              <p className="text-white/40 text-xs mb-2">🎭 Changer le ton</p>
              <div className="flex flex-wrap gap-2 mb-3" data-testid="history-tones">
                {replies.map((r, i) => (
                  <button key={i} onClick={() => setToneIdx(i)} data-testid={`history-tone-${i}`}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${i === toneIdx ? "gold-gradient text-black border-transparent" : "border-white/15 text-white/60 hover:text-white"}`}>
                    {r.style}
                  </button>
                ))}
              </div>
              <div className="card-surface p-3 text-sm text-white/90 whitespace-pre-wrap" data-testid="history-tone-text">
                {current?.text}
              </div>
            </div>
          )}

          {it.kind === "reply" && replies.length === 0 && (d.short || d.developed) && (
            <div className="card-surface p-3 text-sm text-white/90 whitespace-pre-wrap">{d.developed || d.short}</div>
          )}

          {it.kind === "analyze" && (
            <div className="space-y-2 text-sm">
              {(d.interpretations || []).length > 0 && (
                <div><p className="text-white/40 text-xs mb-1">Interprétations</p>
                  <ul className="list-disc list-inside text-white/80 space-y-1">{d.interpretations.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
              )}
              {(d.calm_responses || []).length > 0 && (
                <div><p className="text-white/40 text-xs mb-1 mt-2">Réponses posées</p>
                  <ul className="list-disc list-inside text-white/80 space-y-1">{d.calm_responses.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
              )}
            </div>
          )}

          {it.kind === "content" && (
            <div className="space-y-2 text-sm">
              {d.hook && <div><p className="text-white/40 text-xs">Hook</p><p className="text-white/85">{d.hook}</p></div>}
              {d.script && <div><p className="text-white/40 text-xs mt-2">Script</p><p className="text-white/85 whitespace-pre-wrap">{d.script}</p></div>}
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            <ActionBtn onClick={goReprendre} icon={RotateCw} label="Reprendre" testid="history-act-reprendre" primary />
            <ActionBtn onClick={goVariante} icon={Wand2} label="Nouvelle variante" testid="history-act-variante" />
            <ActionBtn onClick={goReprendre} icon={Pencil} label="Modifier" testid="history-act-modifier" />
            <ActionBtn onClick={() => copy(it.kind === "reply" ? (current?.text || d.short) : (d.script || (d.interpretations || []).join("\n")))}
              icon={Copy} label="Copier" testid="history-act-copier" />
          </div>
        </div>
      </div>
    </div>
  );
}
